/**
 * RecordingsList — Left pane of the recordings page
 *
 * Features:
 * - Date-grouped recording list (Today, Yesterday, This Week, etc.)
 * - Selection checkboxes for bulk operations
 * - Category badge + GAS-share badge
 * - Click or keyboard (Enter/Space) to view detail
 */

import React, { useMemo } from 'react';
import { Mic, Clock, Loader2 } from 'lucide-react';
import CategoryBadge from './CategoryBadge';
import { shareStateOf, isGasCandidate } from './shareStatus';
import { groupByDate, parseRecordedAt } from './dateRanges';

/**
 * GAS-share badge (boundary decision #8). Only work recordings are GAS
 * candidates, so personal ones say so plainly instead of showing a state
 * that implies they're queued for the tenant.
 */
function ShareBadge({ recording }) {
  if (!isGasCandidate(recording)) return null;
  const state = shareStateOf(recording);
  const { Icon } = state;
  return (
    <span className={`rp-share-badge ${state.key}`} title={state.listHint}>
      <Icon size={11} aria-hidden="true" />
      {state.badge}
    </span>
  );
}

// Format date for display
function fmtDate(iso) {
  const d = parseRecordedAt(iso);
  if (!d) return 'No date';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function RecordingsList({
  recordings,
  selectedIds,
  selectedRecordingId,
  isLoading,
  onSelect,
  onToggleSelect,
  onSelectAll,
  footer,
}) {
  const groupedRecordings = useMemo(() => groupByDate(recordings), [recordings]);

  const allSelected = recordings.length > 0 && selectedIds.size === recordings.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < recordings.length;

  return (
    <div className="rp-list">
      <div className="rp-list-header">
        <div className="rp-list-header-left">
          <label className="rp-select-all">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => el && (el.indeterminate = someSelected)}
              onChange={onSelectAll}
              disabled={recordings.length === 0}
            />
            <span>Select all</span>
          </label>
        </div>
        <span className="rp-list-count">
          {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="rp-list-body">
        {isLoading && (
          <div className="rp-list-loading">
            <Loader2 size={24} className="rp-spin" />
            <p>Loading recordings...</p>
          </div>
        )}

        {!isLoading && recordings.length === 0 && (
          <div className="rp-list-empty">
            <Mic size={32} strokeWidth={1.5} />
            <p>No recordings found</p>
            <span>Adjust the filters, or use “Sync from Plaud” to import more</span>
          </div>
        )}

        {!isLoading && groupedRecordings.map(group => (
          <div key={group.label} className="rp-date-group">
            <div className="rp-date-label">{group.label}</div>
            {group.items.map(recording => (
              /* The checkbox is a SIBLING of the open-recording button, not a
                 child of it. Nesting one interactive control inside another is
                 invalid: assistive tech may never expose the inner checkbox,
                 and the outer button's name swallows it. */
              <div
                key={recording.id}
                className={`rp-item ${selectedRecordingId === recording.id ? 'selected' : ''}`}
              >
                <label className="rp-item-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(recording.id)}
                    onChange={() => onToggleSelect(recording.id)}
                    aria-label={`Select ${recording.name}`}
                  />
                </label>
                <button
                  type="button"
                  className="rp-item-main"
                  onClick={() => onSelect(recording)}
                  aria-label={`Open ${recording.name}`}
                  aria-current={selectedRecordingId === recording.id}
                >
                  <span className="rp-item-name" title={recording.name}>
                    {recording.name}
                  </span>
                  <span className="rp-item-meta">
                    <span>{fmtDate(recording.recorded_at)}</span>
                    {recording.duration_formatted && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span><Clock size={10} /> {recording.duration_formatted}</span>
                      </>
                    )}
                    <CategoryBadge
                      category={recording.categoryInfo?.category}
                      confidence={recording.categoryInfo?.confidence}
                      source={recording.categoryInfo?.source}
                    />
                    {/* The account decides which GAS agency tab this summary
                        files under, so it belongs on the row — and an
                        unconfirmed auto-guess has to look different from a
                        decision, because only confirmed ones reach GAS. */}
                    {recording.account_id && (
                      <span
                        className={`rp-account-chip ${recording.account_source === 'user' ? 'confirmed' : 'unconfirmed'}`}
                        title={
                          recording.account_source === 'user'
                            ? `${recording.account_name} — confirmed by you`
                            : `${recording.account_name} — auto-guess, not confirmed; won't appear in GAS until you confirm it`
                        }
                      >
                        {recording.account_source !== 'user' && '? '}
                        {recording.account_acronym || recording.account_name}
                      </span>
                    )}
                    <ShareBadge recording={recording} />
                  </span>
                </button>
              </div>
            ))}
          </div>
        ))}

        {!isLoading && footer}
      </div>
    </div>
  );
}

export default RecordingsList;
